import { useEffect, type RefObject } from 'react';

const DEFAULT_EXTRA_BOTTOM_INSET = 80;

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !window.matchMedia('(min-width: 1024px)').matches;
};

const isTextField = (el: EventTarget | null): el is HTMLElement => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLSelectElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  const type = el.type.toLowerCase();
  return type !== 'button' && type !== 'checkbox' && type !== 'radio' && type !== 'submit';
};

export const getScrollableParent = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const styles = window.getComputedStyle(current);
    const overflowY = styles.overflowY;
    const isScrollable =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      current.scrollHeight > current.clientHeight;
    if (isScrollable) return current;
    current = current.parentElement;
  }
  return null;
};

const getScrollTarget = (field: HTMLElement): HTMLElement => {
  const questionWrapper = field.closest('[data-experience-question]') as HTMLElement | null;
  if (questionWrapper) return questionWrapper;

  if (field instanceof HTMLTextAreaElement || field.tagName === 'TEXTAREA') {
    return field;
  }

  return field;
};

const getVisibleViewportBounds = (extraBottomInset = DEFAULT_EXTRA_BOTTOM_INSET) => {
  const vv = window.visualViewport;
  const visibleTop = (vv?.offsetTop ?? 0) + 12;
  const visibleBottom =
    visibleTop + (vv?.height ?? window.innerHeight) - extraBottomInset;
  return { visibleTop, visibleBottom };
};

const scrollFieldIntoVisibleArea = (
  field: HTMLElement,
  extraBottomInset = DEFAULT_EXTRA_BOTTOM_INSET
) => {
  const scrollTarget = getScrollTarget(field);
  const { visibleTop, visibleBottom } = getVisibleViewportBounds(extraBottomInset);
  const rect = scrollTarget.getBoundingClientRect();
  const scrollParent = getScrollableParent(scrollTarget);
  const visibleHeight = visibleBottom - visibleTop;

  if (!scrollParent) {
    scrollTarget.scrollIntoView({ block: 'center', behavior: 'auto' });
    return;
  }

  if (rect.height > visibleHeight * 0.6) {
    const targetTop =
      rect.top - visibleTop - Math.max(0, (visibleHeight - rect.height) / 2);
    if (Math.abs(targetTop) > 4) {
      scrollParent.scrollBy({ top: targetTop, behavior: 'auto' });
    }
    return;
  }

  const visibleMid = visibleTop + visibleHeight / 2;
  const fieldMid = rect.top + rect.height / 2;
  const isInBottomHalf = fieldMid > visibleMid;

  if (rect.bottom > visibleBottom) {
    const overflow = rect.bottom - visibleBottom + 16;
    scrollParent.scrollBy({ top: overflow, behavior: 'auto' });
    return;
  }

  if (rect.top < visibleTop) {
    scrollParent.scrollBy({ top: rect.top - visibleTop, behavior: 'auto' });
    return;
  }

  if (isInBottomHalf && rect.bottom > visibleBottom - 24) {
    scrollParent.scrollBy({
      top: rect.bottom - (visibleBottom - 24),
      behavior: 'auto',
    });
  }
};

export type MobileFieldScrollOptions = {
  extraBottomInset?: number;
};

export const ensureFieldVisibleOnMobile = (
  field: HTMLElement | null | undefined,
  options?: MobileFieldScrollOptions
) => {
  if (!field || !isMobileViewport()) return;

  const extraBottomInset = options?.extraBottomInset ?? DEFAULT_EXTRA_BOTTOM_INSET;
  const run = () => scrollFieldIntoVisibleArea(field, extraBottomInset);

  window.setTimeout(run, 180);
  window.setTimeout(run, 400);
  window.setTimeout(run, 650);
};

export const useMobileFieldScroll = (
  containerRef: RefObject<HTMLElement | null>,
  options?: MobileFieldScrollOptions
) => {
  const extraBottomInset = options?.extraBottomInset ?? DEFAULT_EXTRA_BOTTOM_INSET;

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const scrollActiveField = (target: EventTarget | null) => {
      if (!isTextField(target)) return;
      if (!(target instanceof Node) || !root.contains(target)) return;
      ensureFieldVisibleOnMobile(target, { extraBottomInset });
    };

    const handleFocusIn = (event: FocusEvent) => {
      scrollActiveField(event.target);
    };

    const handleViewportChange = () => {
      scrollActiveField(document.activeElement);
    };

    root.addEventListener('focusin', handleFocusIn);
    window.visualViewport?.addEventListener('resize', handleViewportChange);
    window.visualViewport?.addEventListener('scroll', handleViewportChange);

    return () => {
      root.removeEventListener('focusin', handleFocusIn);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      window.visualViewport?.removeEventListener('scroll', handleViewportChange);
    };
  }, [containerRef, extraBottomInset]);
};

export const isMobileTextField = isTextField;
